var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Copy, Download, Eye, FlaskConical, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { AdminButton, Badge, Card, DataTable, EmptyState, ErrorNotice, FillLayout, SectionHeader, } from './admin-ui';
import { fetchAllProvidersFull } from './voice-admin-api';
import { downloadJson } from './provider-form/editors';
import { forExport } from './provider-form/provider-form-modal';
/**
 * Provider list.
 *
 * Renders from the LIST summary — the full spec is fetched only when a view or
 * edit screen opens. Rethemed onto the `.vdash` tokens so it follows the
 * console's light/dark switch, which the previous fixed-palette version
 * (`bg-card`, `text-foreground`) could not.
 */
const VERIFICATION_TONE = {
    verified: 'success',
    unverified: 'neutral',
    failed: 'danger',
};
export default function ProviderListPanel({ providers, loading, error, onRetry, onView, onEdit, onTest, onDuplicate, onDelete, onCreate, }) {
    const columns = [
        {
            key: 'provider',
            header: 'Provider',
            render: (p) => (_jsxs("span", { className: 'flex min-w-0 flex-col', children: [_jsx("span", { className: 'truncate text-[13px] font-semibold text-dash-text', children: p.label || p.provider_id }), _jsx("span", { className: 'truncate text-[11px] text-dash-muted', children: p.provider_id })] })),
        },
        {
            key: 'transport',
            header: 'Transport',
            hideOnMobile: true,
            render: (p) => {
                var _a, _b;
                return (_jsxs("span", { className: 'flex flex-wrap justify-end gap-1 aiMd:justify-start', children: [_jsx(Badge, { tone: 'purple', title: 'Speech-to-text transport', children: (_a = p.stt_transport) !== null && _a !== void 0 ? _a : '—' }), _jsx(Badge, { tone: 'cyan', title: 'Text-to-speech transport', children: (_b = p.tts_transport) !== null && _b !== void 0 ? _b : '—' })] }));
            },
        },
        {
            key: 'catalog',
            header: 'Catalogue',
            align: 'right',
            render: (p) => (_jsxs("span", { className: 'whitespace-nowrap tabular-nums text-dash-text2', children: [p.voice_count, " voices \u00B7 ", p.language_count, " langs"] })),
        },
        {
            key: 'status',
            header: 'Status',
            render: (p) => {
                var _a;
                return (_jsxs("span", { className: 'flex flex-wrap justify-end gap-1 aiMd:justify-start', children: [_jsx(Badge, { tone: p.enabled ? 'success' : 'neutral', children: p.enabled ? 'enabled' : 'disabled' }), p.is_default && _jsx(Badge, { tone: 'accent', children: "default" }), _jsx(Badge, { tone: (_a = VERIFICATION_TONE[p.verification_status]) !== null && _a !== void 0 ? _a : 'neutral', title: p.last_test_error ||
                                'A provider must pass a live STT + TTS test before it can be scheduled.', children: p.verification_status })] }));
            },
        },
        {
            key: 'actions',
            header: '',
            align: 'right',
            render: (p) => (_jsxs("span", { className: 'flex flex-wrap justify-end gap-1.5', children: [_jsx(AdminButton, { icon: _jsx(Eye, { className: 'h-3.5 w-3.5' }), onClick: () => onView(p.provider_id), children: "View" }), _jsx(AdminButton, { icon: _jsx(FlaskConical, { className: 'h-3.5 w-3.5' }), onClick: () => onTest(p), children: "Test" }), _jsx(AdminButton, { icon: _jsx(Pencil, { className: 'h-3.5 w-3.5' }), onClick: () => onEdit(p.provider_id), children: "Edit" }), _jsx(AdminButton, { title: 'Duplicate', "aria-label": `Duplicate ${p.provider_id}`, icon: _jsx(Copy, { className: 'h-3.5 w-3.5' }), onClick: () => onDuplicate(p.provider_id) }), _jsx(AdminButton, { tone: 'danger', icon: _jsx(Trash2, { className: 'h-3.5 w-3.5' }), onClick: () => onDelete(p.provider_id), children: "Delete" })] })),
        },
    ];
    // The list holds SUMMARIES, so exporting everything has to re-fetch the full
    // records. Keys are never included — only the masked hint exists client side.
    const [exporting, setExporting] = useState(false);
    const exportAll = () => __awaiter(this, void 0, void 0, function* () {
        setExporting(true);
        try {
            const full = yield fetchAllProvidersFull();
            downloadJson('voice-providers.json', (full !== null && full !== void 0 ? full : []).map(forExport));
        }
        finally {
            setExporting(false);
        }
    });
    if (error) {
        return (_jsx(Card, { children: _jsx(ErrorNotice, { title: 'Could not load voice providers.', error: error, onRetry: onRetry }) }));
    }
    if (!loading && !providers.length) {
        return (_jsxs(Card, { children: [_jsx(EmptyState, { title: 'No voice providers configured yet', hint: 'A provider supplies both the STT and TTS legs for cost-optimized interviews.' }), _jsx("div", { className: 'mt-3 flex justify-center', children: _jsx(AdminButton, { tone: 'primary', icon: _jsx(Plus, { className: 'h-4 w-4' }), onClick: onCreate, children: "Add your first provider" }) })] }));
    }
    return (_jsx(FillLayout, { children: _jsxs(Card, { fill: true, children: [_jsx(SectionHeader, { title: 'Voice providers', subtitle: 'One provider supplies BOTH the STT and TTS legs \u2014 they are never mixed vendors. A provider must pass a live test before it can be scheduled.', action: _jsx(AdminButton, { disabled: exporting, icon: exporting ? (_jsx(Loader2, { className: 'h-3.5 w-3.5 animate-spin' })) : (_jsx(Download, { className: 'h-3.5 w-3.5' })), onClick: exportAll, children: "Export all" }) }), _jsx(DataTable, { fill: true, columns: columns, rows: providers, rowKey: (p) => p.provider_id, onRowClick: (p) => onView(p.provider_id), loading: loading, loadingLabel: 'Loading voice providers' })] }) }));
}
